import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrganisationServiceSpa } from 'src/app/generated/services/organisations/organisationSpa';
import { Organisation } from 'src/app/generated/services/organisations/organisation';

@Injectable({
  providedIn: 'root'
})
export class OrganisationService {

  constructor(private _organisationsService: OrganisationServiceSpa) { }

  get(): Observable<Organisation[]> {
    return from(this._organisationsService.getAllOrganisations({}).then(o => o.organisations));
  }

  getPorts(): Observable<Organisation[]> {
    return this.get().pipe(map(port => port.filter(o => (o.properties['isAnchorage'] === true ||
        o.properties['isPort'] === true)).sort((a, b) =>
      a.nameTranslations['en'].localeCompare(b.nameTranslations['en']))));
  }

  getSurveyors(): Observable<Organisation[]> {
    return this.get().pipe(map(
      surveyors => surveyors.filter(organisation => organisation.properties['isSurvey'] === true)));
  }

  getDomestics(): Observable<Organisation[]> {
    return this.get().pipe(map(
      surveyers => surveyers.filter(organisation => organisation.properties['domestic'] === true)));
  }

  getSupplier(): Observable<Organisation[]> {
    return this.get().pipe(map(
      supplier => supplier.filter(organisation => organisation.properties['isBunkerSupplier'] === true)));
  }
  getFreshWaterSupplier(): Observable<Organisation[]> {
    return this.get().pipe(map(
      supplier => supplier.filter(organisation => organisation.properties['isFreshWaterSupplier'] === true)));
  }

  getOilSupplier(): Observable<Organisation[]> {
    return this.get().pipe(map(
      supplier => supplier.filter(organisation => organisation.properties['isOilSupplier'] === true)));
  }

  getShippers(): Observable<Organisation[]> {
    return this.get().pipe(map(
      surveyors => surveyors.filter(organisation => organisation.properties['isShipper'] === true)));
  }

  getAgents(): Observable<Organisation[]> {
    return this.get().pipe(map(
      surveyors => surveyors.filter(organisation => organisation.properties['isShippingAgent'] === true)));
  }

  getOrganisationTimezone(organisationId: string, organisations: Organisation[]): string | undefined {
    return organisations.find(o => o.name === organisationId)?.timezone;
  }

  getLoadingsLocations(): Observable<Organisation[]> {
    return this.get().pipe(map(loadings => loadings.filter(o => (o.properties['isLoading'] === true))));
  }

  getDischargeLocations(): Observable<Organisation[]> {
    return this.get().pipe(map(discharges => discharges.filter(o => (o.properties['isUnloading'] === true))));
  }

  getSellers(): Observable<Organisation[]> {
    return this.get().pipe(map(sellers => sellers.filter(o => (o.properties['isSeller'] === true))));
  }

  getBuyers(): Observable<Organisation[]> {
    return this.get().pipe(map(buyers => buyers.filter(o => (o.properties['isBuyer'] === true))));
  }

  getTransporters(): Observable<Organisation[]> {
    return this.get().pipe(map(transporters => transporters.filter(o => (o.properties['isTransporter'] === true))));
  }

  getRootOrganisations(): Observable<Organisation[]> {
    return this.get().pipe(map(transporters => transporters
      .filter(o => ((o.properties['isLoading'] === false || !o.properties['isLoading'])
              && (o.properties['isUnloading'] === false || !o.properties['isUnloading'])
              && (o.properties['isAnchorage'] === false || !o.properties['isAnchorage'])
              && (o.properties['isPort'] === false || !o.properties['isPort'])
              && (o.properties['isSurvey'] === false || !o.properties['isSurvey'])
              && (o.properties['isConsignee'] === false || !o.properties['isConsignee'])
      ))));
  }

  getRootOrganisationsLoading(organisations: Organisation[]): Organisation[] {
    return organisations
      .filter(o => ((o.properties['isLoading'] === false || !o.properties['isLoading'])
              && (o.properties['isUnloading'] === false || !o.properties['isUnloading'])
              && (o.properties['isAnchorage'] === false || !o.properties['isAnchorage'])
              && (o.properties['isPort'] === false || !o.properties['isPort'])
              && (o.properties['isSurvey'] === false || !o.properties['isSurvey'])
              && (o.properties['isConsignee'] === false || !o.properties['isConsignee'])
      ));
  }

  getConsignee(): Observable<Organisation[]> {
    return this.get().pipe(map(consignee => consignee.filter(o => (o.properties['isConsignee'] === true))));
  }

  getOrganisationIdToOrganisationMap(): Observable<Map<string, Organisation>> {
    return this.get().pipe(map(orgs => new Map(orgs.map(o => [o.name, o]))));
  }

  getOrganisationIdToOrganisationNameMap(): Observable<Map<string, string>> {
    return this.get().pipe(map(orgs => new Map(orgs.map(o => [o.name, o.nameTranslations['en']]))));
  }

  getCountryCode(organisationId: string): string[] {
    switch (organisationId) {
    case 'ukraine': return ['fi', 'fi-ua'];
    case 'russia': return ['fi', 'fi-ru'];
    case 'philippine': return ['fi', 'fi-ph'];
    case 'iran': return ['fi', 'fi-ir'];
    case 'india': return ['fi', 'fi-in'];
    case 'azerbaijan': return ['fi', 'fi-az'];
    case 'moldova': return ['fi', 'fi-md'];
    case 'romania': return ['fi', 'fi-ro'];
    case 'georgia': return ['fi', 'fi-ge'];
    case 'bulgaria': return ['fi', 'fi-bg'];
    default: return ['fi', 'fx'];
    }
  }
}
